import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { DatafieldTypes } from 'src/app/models/database/datafield-type.model';
import { Datafield, Datafields } from 'src/app/models/database/datafield.model';
import { DatasheetSession, DatasheetSessions } from 'src/app/models/database/datasheet-session.model';
import { Datasheet, Datasheets } from 'src/app/models/database/datasheet.model';
import { Id } from 'src/app/models/database/types/id.model';
import { HttpService } from '../http-service.service';

@Injectable({
  providedIn: 'root'
})
export class PepService extends HttpService {

  constructor(private httpClient: HttpClient) {
    super('pep')
  }

  registerDatasheet(datasheet: Datasheet): Observable<void> {
    return this.httpClient.post<void>(`${this.url}/datasheet`, { datasheet })
  }

  registerDatasheetSession(datasheetSession: DatasheetSession): Observable<void> {
    return this.httpClient.post<void>(`${this.url}/datasheet/session`, { datasheetSession })
  }

  registerDatafield(datafield: Datafield): Observable<void> {
    return this.httpClient.post<void>(`${this.url}/datafield`, { datafield })
  }

  getClinicDatasheets(filter?: any): Observable<Datasheets> {
    let url: string = `${this.url}/datasheets`
    if (filter) {
      url += this.implementQueries(filter)
    }
    return this.httpClient.get<Datasheets>(url)
  }

  getDatasheet(datasheetId: Id): Observable<Datasheet> {
    return this.httpClient.get<Datasheet>(`${this.url}/datasheet/${datasheetId}`)
  }

  getDatasheetData(datasheetId: Id): Observable<{ session: DatasheetSession, datafields: Datafields }[]> {
    return this.httpClient.get<{ session: DatasheetSession, datafields: Datafields }[]>(`${this.url}/datasheet/${datasheetId}/data`)
  }

  getDatasheetSessions(datasheetId: Id): Observable<DatasheetSessions> {
    return this.httpClient.get<DatasheetSessions>(`${this.url}/datasheet/${datasheetId}/session`)
  }

  getDatasheetSessionFields(datasheetSessionId: Id): Observable<any> {
    return this.httpClient.get<any>(`${this.url}/session/${datasheetSessionId}/datafields`)
  }

  getDatafield(datafieldId: Id): Observable<Datafield> {
    return this.httpClient.get<Datafield>(`${this.url}/datafield/${datafieldId}`)
  }

  getDatafieldTypes(): Observable<DatafieldTypes> {
    return this.httpClient.get<DatafieldTypes>(`${this.url}/datafield/types`)
  }

  editDatasheet(datasheet: Datasheet): Observable<void> {
    return this.httpClient.put<void>(`${this.url}/datasheet/${datasheet.id}`, { datasheet })
  }

  editDatasheetSession(datasheetSession: DatasheetSession): Observable<void> {
    return this.httpClient.put<void>(`${this.url}/datasheet/session`, { datasheetSession })
  }

  editDatafield(datafield: Datafield): Observable<void> {
    return this.httpClient.put<void>(`${this.url}/datafield/${datafield._id}`, { datafield })
  }

  reorderDatasheetSections(datasheetId: number, datasheetSections: { id: number, order: number }[]): Observable<number> {
    return this.httpClient.put<number>(`${this.url}/${datasheetId}/reorder-section`, { sections: datasheetSections })
      .pipe(
        take(1),
        catchError((error) => {
          return of(null)
        })
      )
  }

  reorderSectionFields(sectionId: number, sectionfields: { _id: string, order: number }[]): Observable<number> {
    return this.httpClient.put<number>(`${this.url}/${sectionId}/reorder-section-field`, { sectionfields })
      .pipe(
        take(1),
        catchError((error) => {
          return of(null)
        })
      )
  }

  deleteDatasheet(datasheetId: Id): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}/datasheet/${datasheetId}`)
  }

  deleteDatasheetSession(datasheetSessionId: Id): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}/datasheet/session/${datasheetSessionId}`)
  }

  deleteDatafield(datafieldId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}/datafield/${datafieldId}`)
  }

  nameExists(name: string, datasheetId: number = null): Observable<void> {
    let url: string = `${this.url}/validate-name?name=${name}`
    if (datasheetId) {
      url += `&datasheetId=${datasheetId}`
    }
    return this.httpClient.head<void>(url)
  }

  sectionExists(datasheetId: number, section: string, datasheetSectionId?: number): Observable<void> {
    let url: string = `${this.url}/${datasheetId}/validate-section?description=${section}`
    if (datasheetSectionId) {
      url += `&datasheetSessionId=${datasheetSectionId}`
    }
    return this.httpClient.head<void>(url)
  }

  datafieldExistsInSection(sectionId: number, datafieldDescription: string, datafieldId?: string): Observable<void> {
    let url: string = `${this.url}/${sectionId}/validate-datafield?description=${datafieldDescription}`
    if (datafieldId) {
      url += `&datafieldId=${datafieldId}`
    }
    return this.httpClient.head<void>(url)
  }
}